import type { ComponentChildren } from "preact";

export interface MetricHelp {
  title?: string;
  body?: string;
  note?: string;
}

/**
 * [標籤, 數值, 副標, 說明]。說明是選填，有的話才長出問號按鈕。
 *
 * 數值不限於字串：監督者在揭曉日之前看到的是 `<Arrow>`，而箭頭需要
 * `aria-label` 才讀得出意義，塞不進一個字串。
 */
export type MetricCardData = [
  label: string,
  value: ComponentChildren,
  small: string,
  help?: MetricHelp
];

/**
 * 說明按鈕的展開狀態由外部管理（同一時間只開一個，點外面或 Esc 關閉），
 * 元件本身不持有狀態——否則 Preact 重繪時 class 會被洗掉。
 */
export function MetricCard({ data, open, onToggleHelp }: {
  data: MetricCardData;
  open: boolean;
  onToggleHelp: (label: string) => void;
}) {
  const [label, value, small, help] = data;
  return (
    <div class={`metric-card ${help ? "has-help" : ""}`}>
      <div class="metric-card-head">
        <span>{label}</span>
        {help && (
          <div class={`metric-help-wrap ${open ? "is-open" : ""}`}>
            <button
              type="button" class="metric-help-btn"
              aria-label={`查看${label}說明`} aria-expanded={open}
              onClick={event => { event.preventDefault(); event.stopPropagation(); onToggleHelp(label); }}
            >?</button>
            <div class="metric-tooltip" role="tooltip">
              <b>{help.title || label}</b>
              <p>{help.body || ""}</p>
              {help.note && <small>{help.note}</small>}
            </div>
          </div>
        )}
      </div>
      <strong>{value}</strong>
      <small>{small}</small>
    </div>
  );
}

export function MetricCards({ cards, openHelp, onToggleHelp }: {
  cards: MetricCardData[];
  openHelp: string | null;
  onToggleHelp: (label: string) => void;
}) {
  return (
    <>
      {cards.map((data, index) => (
        <MetricCard key={index} data={data} open={openHelp === data[0]} onToggleHelp={onToggleHelp} />
      ))}
    </>
  );
}
